/**
 * Abgeleitete Werte aus dem App-Store: effektive Rechte, Anzeigenamen, Ungelesen-Status, sortierte Kanäle.
 * Reine Funktionen auf `AppData` + passende Hooks.
 */
import {
  compareSnowflakes,
  computeBasePermissions,
  computePermissions,
  DM_PERMISSIONS,
  hasPermission,
  isDm,
  isThread,
  Permissions,
  threadPermissions,
  type ChannelDto,
  type MemberDto,
  type PermissionName,
  type RoleDto,
  type UserDto,
} from '@lagerfeuer/shared';
import { useEffect, useState } from 'react';
import { useAppStore } from './app';
import type { AppData } from './types';

/** Basis-Rollen eines Mitglieds (inkl. @everyone, id = serverId). */
function memberRoles(roles: RoleDto[], serverId: string, member: Pick<MemberDto, 'roleIds'>): RoleDto[] {
  const ids = new Set([serverId, ...member.roleIds]);
  return roles.filter((r) => ids.has(r.id) || r.isEveryone);
}

function isTimedOut(member: Pick<MemberDto, 'timeoutUntil'>): boolean {
  return !!member.timeoutUntil && new Date(member.timeoutUntil).getTime() > Date.now();
}

/** Serverweite Rechte des eigenen Nutzers. */
export function serverPermissions(s: AppData, serverId: string): bigint {
  const server = s.servers[serverId];
  if (!server || !s.me) return 0n;
  const isOwner = server.ownerId === s.me.id;
  return computeBasePermissions(memberRoles(s.roles[serverId] ?? [], serverId, server.me), isOwner);
}

/** Effektive Overrides eines Kanals (synchronisierte Kanäle nutzen die Kategorie, D16). */
export function effectiveOverwrites(s: AppData, ch: ChannelDto): ChannelDto['permissionOverwrites'] {
  if (ch.permissionsSynced && ch.parentId) {
    const parent = s.channels[ch.parentId];
    if (parent && parent.type === 'CATEGORY') return parent.permissionOverwrites;
  }
  return ch.permissionOverwrites;
}

/** Effektive Rechte des eigenen Nutzers in einem Kanal (Threads über den Elternkanal, DMs fest). */
export function channelPermissions(s: AppData, channelId: string): bigint {
  const ch = s.channels[channelId];
  if (!ch || !s.me) return 0n;
  if (isDm(ch.type)) return DM_PERMISSIONS;
  if (isThread(ch.type)) {
    if (!ch.parentId) return 0n;
    const parent = s.channels[ch.parentId];
    if (!parent?.serverId) return DM_PERMISSIONS;
    return threadPermissions(channelPermissions(s, ch.parentId), { locked: ch.thread?.locked, archived: ch.thread?.archived });
  }
  if (!ch.serverId) return 0n;
  const server = s.servers[ch.serverId];
  if (!server) return 0n;
  return computePermissions(memberRoles(s.roles[ch.serverId] ?? [], ch.serverId, server.me), effectiveOverwrites(s, ch), server.ownerId === s.me.id, {
    everyoneRoleId: ch.serverId,
    memberId: s.me.id,
    timedOut: isTimedOut(server.me),
    channelKind: ch.type === 'VOICE' ? 'voice' : 'text',
  });
}

export function can(perms: bigint, ...names: PermissionName[]): boolean {
  return names.every((n) => hasPermission(perms, Permissions[n]));
}

export function useServerPermissions(serverId: string | undefined): bigint {
  return useAppStore((s) => (serverId ? serverPermissions(s, serverId) : 0n));
}

export function useChannelPermissions(channelId: string | undefined): bigint {
  useTimeoutExpiry(useAppStore((s) => (channelId ? s.channels[channelId]?.serverId : null)));
  return useAppStore((s) => (channelId ? channelPermissions(s, channelId) : 0n));
}

/** Größte sichere setTimeout-Verzögerung (~24,8 Tage). */
const MAX_TIMER_MS = 2 ** 31 - 1;

/**
 * Rechte hängen von der Uhr ab (Timeout): beim Ablauf des eigenen Timeouts neu rendern, damit die
 * Selektoren neu rechnen (sonst bliebe z. B. der Composer nach Timeout-Ende gesperrt).
 */
export function useTimeoutExpiry(serverId: string | null | undefined): void {
  const until = useAppStore((s) => (serverId ? s.servers[serverId]?.me.timeoutUntil : null));
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!until) return;
    const ms = new Date(until).getTime() - Date.now();
    if (!(ms > 0)) return;
    // Lange Timeouts in Etappen (setTimeout läuft sonst sofort über).
    const t = setTimeout(() => setTick((n) => n + 1), Math.min(ms + 50, MAX_TIMER_MS));
    return () => clearTimeout(t);
  });
}

/** Höchste Rollenposition eines Mitglieds (für Hierarchie-Prüfungen in der UI). */
export function highestRolePosition(roles: RoleDto[], member: Pick<MemberDto, 'roleIds'>): number {
  let max = 0;
  for (const r of roles) if (member.roleIds.includes(r.id) && r.position > max) max = r.position;
  return max;
}

/** Farbe der höchsten farbigen Rolle als CSS-Farbe (oder null). */
export function memberColor(roles: RoleDto[] | undefined, roleIds: string[] | undefined): string | null {
  if (!roles || !roleIds?.length) return null;
  for (const r of roles) {
    if (r.color && roleIds.includes(r.id)) return `#${r.color.toString(16).padStart(6, '0')}`;
  }
  return null;
}

export function displayName(user: Pick<UserDto, 'username' | 'displayName'> | null | undefined, member?: Pick<MemberDto, 'nickname'> | null): string {
  if (!user) return 'Unbekannt';
  return member?.nickname || user.displayName || user.username;
}

/**
 * Alle Teilnehmenden einer DM/Gruppe inklusive mir. `recipients` enthält den Betrachter je nach Quelle nicht
 * (REST/READY liefern „die anderen" wie Discord, Kanal-Ereignisse alle) – hier immer genau einmal dabei.
 */
export function dmParticipants(ch: Pick<ChannelDto, 'recipients'>, me: UserDto | null | undefined): UserDto[] {
  const list = ch.recipients ?? [];
  if (!me || list.some((u) => u.id === me.id)) return list;
  return [me, ...list];
}

/** Anzeigename eines DM-/Gruppenkanals. */
export function dmName(ch: ChannelDto, meId: string | undefined): string {
  if (ch.name) return ch.name;
  const others = (ch.recipients ?? []).filter((u) => u.id !== meId);
  if (others.length === 0) return 'Notizen an mich';
  return others.map((u) => u.displayName || u.username).join(', ');
}

/** Ungelesen = lastMessageId > lastReadMessageId (Vertrag 4.3). */
export function isUnread(s: AppData, channelId: string): boolean {
  const ch = s.channels[channelId];
  if (!ch?.lastMessageId) return false;
  const rs = s.readStates[channelId];
  if (!rs?.lastReadMessageId) return true;
  return compareSnowflakes(ch.lastMessageId, rs.lastReadMessageId) > 0;
}

export function mentionCount(s: AppData, channelId: string): number {
  return s.readStates[channelId]?.mentionCount ?? 0;
}

/** Stummgeschaltet (Kanal oder Server). */
export function isMuted(s: AppData, scope: 'SERVER' | 'CHANNEL', id: string): boolean {
  const setting = s.notificationSettings.find((x) => x.scopeType === scope && x.scopeId === id);
  if (!setting?.muted) return false;
  return !setting.mutedUntil || new Date(setting.mutedUntil).getTime() > Date.now();
}

/** Sichtbare Serverkanäle, sortiert und nach Kategorien gruppiert. */
export interface ChannelGroup {
  category: ChannelDto | null;
  channels: ChannelDto[];
}

const byPosition = (a: ChannelDto, b: ChannelDto) => a.position - b.position || compareSnowflakes(a.id, b.id);
const voiceLast = (a: ChannelDto, b: ChannelDto) => Number(a.type === 'VOICE') - Number(b.type === 'VOICE');

export function groupServerChannels(s: AppData, serverId: string): ChannelGroup[] {
  const all = Object.values(s.channels).filter((c) => c.serverId === serverId && !isThread(c.type) && !isDm(c.type));
  const visible = all.filter((c) => c.type === 'CATEGORY' || can(channelPermissions(s, c.id), 'VIEW_CHANNEL'));
  const cats = visible.filter((c) => c.type === 'CATEGORY').sort(byPosition);
  const loose = visible.filter((c) => c.type !== 'CATEGORY' && (!c.parentId || !cats.some((k) => k.id === c.parentId)));
  const groups: ChannelGroup[] = [];
  if (loose.length) groups.push({ category: null, channels: loose.sort(byPosition).sort(voiceLast) });
  for (const cat of cats) {
    const children = visible.filter((c) => c.parentId === cat.id && c.type !== 'CATEGORY').sort(byPosition).sort(voiceLast);
    groups.push({ category: cat, channels: children });
  }
  return groups;
}

/** Erster lesbarer Textkanal eines Servers (Standardziel beim Öffnen). */
export function defaultChannelId(s: AppData, serverId: string): string | null {
  const server = s.servers[serverId];
  if (server?.systemChannelId && s.channels[server.systemChannelId] && can(channelPermissions(s, server.systemChannelId), 'VIEW_CHANNEL')) {
    return server.systemChannelId;
  }
  for (const g of groupServerChannels(s, serverId)) {
    const c = g.channels.find((x) => x.type === 'TEXT' || x.type === 'ANNOUNCEMENT' || x.type === 'FORUM');
    if (c) return c.id;
  }
  return null;
}

type ServerUnread = { unread: boolean; mentions: number };
/** Memo je Server: Ergebnis bleibt gültig, solange sich die (strukturell geteilten) Eingaben nicht ändern. */
const unreadCache = new Map<string, { deps: unknown[]; value: ServerUnread }>();

/**
 * Ungelesen-Zusammenfassung eines Servers für die Serverleiste. Memoisiert: läuft bei jeder Store-Änderung
 * (Tippen, Präsenzen …) je Server, rechnet aber nur neu, wenn Kanäle, Lesestände, Stummschaltungen,
 * Thread-Mitgliedschaften, Rollen oder die eigene Mitgliedschaft sich geändert haben.
 */
export function serverUnread(s: AppData, serverId: string): ServerUnread {
  const deps = [s.channels, s.readStates, s.notificationSettings, s.joinedThreadIds, s.servers[serverId], s.roles[serverId], s.me];
  const hit = unreadCache.get(serverId);
  if (hit && hit.deps.length === deps.length && hit.deps.every((d, i) => d === deps[i])) return hit.value;
  const value = computeServerUnread(s, serverId);
  unreadCache.set(serverId, { deps, value });
  return value;
}

function computeServerUnread(s: AppData, serverId: string): ServerUnread {
  let unread = false;
  let mentions = 0;
  const muted = isMuted(s, 'SERVER', serverId);
  for (const c of Object.values(s.channels)) {
    if (c.serverId !== serverId || c.type === 'CATEGORY' || c.type === 'VOICE') continue;
    mentions += mentionCount(s, c.id);
    // Nur geladene (z. B. aus Thread-Listen) Threads ohne Mitgliedschaft zählen nicht als ungelesen.
    if (isThread(c.type) && !s.joinedThreadIds.includes(c.id)) continue;
    if (!unread && !muted && !isMuted(s, 'CHANNEL', c.id) && isUnread(s, c.id) && can(channelPermissions(s, c.id), 'VIEW_CHANNEL')) unread = true;
  }
  return { unread, mentions };
}

let totalCache: { deps: unknown[]; value: ServerUnread } | null = null;

/**
 * Gesamtstand für den Fenstertitel (D35): Erwähnungen aller Server und DMs, ungelesen = irgendein nicht
 * stummgeschalteter Kanal/DM ungelesen. Memoisiert wie `serverUnread`.
 */
export function totalUnread(s: AppData): ServerUnread {
  const deps = [s.channels, s.readStates, s.notificationSettings, s.joinedThreadIds, s.servers, s.roles, s.me, s.dmIds];
  if (totalCache && totalCache.deps.every((d, i) => d === deps[i])) return totalCache.value;
  let unread = false;
  let mentions = 0;
  for (const serverId of Object.keys(s.servers)) {
    const u = serverUnread(s, serverId);
    mentions += u.mentions;
    unread ||= u.unread;
  }
  for (const id of s.dmIds) {
    mentions += mentionCount(s, id);
    if (!unread && !isMuted(s, 'CHANNEL', id) && isUnread(s, id)) unread = true;
  }
  const value = { unread: unread || mentions > 0, mentions };
  totalCache = { deps, value };
  return value;
}

/** Mitglied (aus dem Store) eines Nutzers in einem Server. */
export function useMember(serverId: string | null | undefined, userId: string | null | undefined): MemberDto | undefined {
  return useAppStore((s) => (serverId && userId ? s.members[serverId]?.[userId] : undefined));
}
